// src/pages/AdminLegacyPending.tsx
import React, { useEffect, useMemo, useState } from "react";
import { FaLink } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

type PendingRow = {
  id: number;
  studentFirstName: string;
  studentLastName: string;
  studentEmail: string;
  courseTitle: string;
  enrollmentDate: string;
  status: string;
};

type TokenInfo = {
  token: string;
  courseId: number;
  remainingUses: number | null; // null => unlimited
  expiryTime: string; // ISO
};

export default function AdminLegacyPending() {
  // Pending enrollments
  const [rows, setRows] = useState<PendingRow[]>([]);
  const [loading, setLoading] = useState(false);

  // Token generator
  const [courseId, setCourseId] = useState<string>("");
  const [maxUses, setMaxUses] = useState<string>("0"); // 0 => unlimited
  const [validityMin, setValidityMin] = useState<string>("1440");
  const [genResult, setGenResult] = useState<{
    link: string;
    token: string;
  } | null>(null);

  // Tokens + titles
  const [tokens, setTokens] = useState<TokenInfo[]>([]);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [courseNameMap, setCourseNameMap] = useState<Record<number, string>>(
    {}
  );

  const API = process.env.REACT_APP_API_BASE_URL;

  const loadPending = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/legacy/pending`, {
        credentials: "include",
      });
      const data = await res.json();
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  const loadTokens = async () => {
    setLoadingTokens(true);
    try {
      const res = await fetch(`${API}/api/admin/legacy/shared-tokens`, {
        credentials: "include",
      });
      const data: TokenInfo[] = await res.json();
      setTokens(data);
      // After tokens arrive, try to fill in course names:
      const ids = Array.from(new Set(data.map((t) => t.courseId)));
      await preloadCourseNames(ids);
    } finally {
      setLoadingTokens(false);
    }
  };

  const preloadCourseNames = async (ids: number[]) => {
    // Skip already-known IDs
    const need = ids.filter((id) => !(id in courseNameMap));
    if (need.length === 0) return;

    const newMap: Record<number, string> = {};
    for (const id of need) {
      try {
        // Adjust to your actual endpoint shape if different:
        //  - If you have /api/courses/{id} returning { title: string }
        const r = await fetch(`${API}/api/courses/${id}`, {
          credentials: "include",
        });
        if (r.ok) {
          const json = await r.json();
          // Try common keys
          const name =
            json?.title || json?.name || json?.courseTitle || `Course ${id}`;
          newMap[id] = String(name);
        } else {
          newMap[id] = `Course ${id}`;
        }
      } catch {
        newMap[id] = `Course ${id}`;
      }
    }
    setCourseNameMap((m) => ({ ...m, ...newMap }));
  };

  useEffect(() => {
    loadPending();
    loadTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activate = async (id: number) => {
    const res = await fetch(`${API}/api/admin/legacy/activate/${id}`, {
      method: "POST",
      credentials: "include",
    });
    if (res.ok) loadPending();
  };

  const generate = async (e: React.FormEvent) => {
    e.preventDefault();
    setGenResult(null);
    if (!courseId) return;

    const params = new URLSearchParams({
      courseId: String(Number(courseId)),
      maxUses: String(Number(maxUses)), // 0 => unlimited
      validityMin: String(Number(validityMin)), // e.g., 1440 = 24h
    }).toString();

    const res = await fetch(
      `${API}/api/admin/legacy/generate-multi-registration-token?${params}`,
      {
        method: "POST",
        credentials: "include",
      }
    );
    if (res.ok) {
      const data = await res.json();
      setGenResult({ link: data.link, token: data.token });
      // Preload this course name if we can
      const cid = Number(courseId);
      await preloadCourseNames([cid]);
      loadTokens(); // refresh list
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Copied!");
    } catch {
      window.prompt("Copy the link:", text);
    }
  };

  const revoke = async (token: string) => {
    if (!window.confirm("Revoke this token? It will stop working immediately."))
      return;
    const res = await fetch(
      `${API}/api/admin/legacy/shared-tokens/${encodeURIComponent(token)}`,
      {
        method: "DELETE",
        credentials: "include",
      }
    );
    if (res.ok) loadTokens();
  };

  const hasTokens = useMemo(() => tokens.length > 0, [tokens]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaLink />}
        title="Legacy Enrollment Admin"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Legacy Pending & Links" },
        ]}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-8">
        {/* Generator */}
        <section className="bg-white rounded-lg shadow p-5">
          <h2 className="text-xl font-semibold mb-3">
            Generate Legacy Registration Link
          </h2>
          <form
            onSubmit={generate}
            className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end"
          >
            <div className="md:col-span-2">
              <label className="block text-sm mb-1">Course ID</label>
              <input
                className="border rounded px-3 py-2 w-full"
                placeholder="e.g., 5"
                value={courseId}
                onChange={(e) => setCourseId(e.target.value)}
                required
              />
              {!!Number(courseId) && courseNameMap[Number(courseId)] && (
                <div className="text-xs text-gray-500 mt-1">
                  Course:{" "}
                  <span className="font-medium">
                    {courseNameMap[Number(courseId)]}
                  </span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm mb-1">
                Max Uses (0 = unlimited)
              </label>
              <input
                className="border rounded px-3 py-2 w-full"
                placeholder="0"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Validity (minutes)</label>
              <input
                className="border rounded px-3 py-2 w-full"
                placeholder="1440"
                value={validityMin}
                onChange={(e) => setValidityMin(e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <button className="bg-[#009fe3] hover:bg-[#007cb5] text-white rounded px-4 py-2 w-full transition">
                Generate
              </button>
            </div>
          </form>

          {genResult && (
            <div className="mt-3 p-3 border rounded bg-gray-50">
              <div className="text-sm mb-1">New Link:</div>
              <div className="flex gap-2 items-center">
                <code className="text-xs break-all">{genResult.link}</code>
                <button
                  className="border rounded px-2 py-1 text-xs"
                  onClick={() => copy(genResult.link)}
                >
                  Copy
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Active Tokens */}
        <section className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold mb-3">Active Legacy Links</h2>
            <button
              onClick={loadTokens}
              className="text-sm border rounded px-3 py-1 hover:bg-gray-50 transition"
            >
              Refresh
            </button>
          </div>
          <div className="overflow-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Course ID</th>
                  <th className="px-3 py-2 text-left">Course Name</th>{" "}
                  {/* NEW */}
                  <th className="px-3 py-2 text-left">Link</th>
                  <th className="px-3 py-2 text-left">Uses Left</th>
                  <th className="px-3 py-2 text-left">Expires</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {loadingTokens ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-6 text-center text-gray-500"
                    >
                      Loading…
                    </td>
                  </tr>
                ) : !hasTokens ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-6 text-center text-gray-500"
                    >
                      No active links
                    </td>
                  </tr>
                ) : (
                  tokens.map((t) => {
                    const link = `${window.location.origin}/legacy-register?token=${t.token}`;
                    const uses =
                      t.remainingUses === null
                        ? "unlimited"
                        : String(t.remainingUses);
                    const cname = courseNameMap[t.courseId] ?? "—";
                    return (
                      <tr key={t.token} className="border-t align-top">
                        <td className="px-3 py-2">{t.courseId}</td>
                        <td className="px-3 py-2">{cname}</td> {/* NEW */}
                        <td className="px-3 py-2">
                          <div className="flex gap-2 items-center">
                            <code className="text-xs break-all">{link}</code>
                            <button
                              className="border rounded px-2 py-1 text-xs"
                              onClick={() => copy(link)}
                            >
                              Copy
                            </button>
                          </div>
                        </td>
                        <td className="px-3 py-2">{uses}</td>
                        <td className="px-3 py-2">
                          {new Date(t.expiryTime).toLocaleString()}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            className="border rounded px-2 py-1 text-xs"
                            onClick={() => revoke(t.token)}
                          >
                            Revoke
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Pending enrollments */}
        <section className="bg-white rounded-lg shadow p-5">
          <h2 className="text-lg font-semibold mb-3">
            Pending Legacy Enrollments
          </h2>
          {loading ? (
            <div>Loading…</div>
          ) : (
            <div className="overflow-auto border rounded">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Student</th>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="px-3 py-2 text-left">Course</th>
                    <th className="px-3 py-2 text-left">Registered</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="px-3 py-2">
                        {r.studentFirstName} {r.studentLastName}
                      </td>
                      <td className="px-3 py-2">{r.studentEmail}</td>
                      <td className="px-3 py-2">{r.courseTitle}</td>
                      <td className="px-3 py-2">
                        {new Date(r.enrollmentDate).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => activate(r.id)}
                          className="px-3 py-1 rounded bg-green-600 text-white"
                        >
                          Activate
                        </button>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-3 py-6 text-center text-gray-500"
                      >
                        No pending legacy enrollments
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
